import { useState } from "react";
import { DAY_BLOCK_PARTS, setDayBlock, type DayPart } from "./dayBlock";
import { Modal } from "./Modal";

export function DayBlockButton({
  day,
  label,
  parts,
  linked,
  ready,
  onChange,
}: {
  day: string;
  label: string;
  parts: DayPart[];
  linked: boolean;
  ready: boolean;
  onChange: (day: string, parts: DayPart[], linked: boolean, ready: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const blocked = parts.length > 0;
  const caption = DAY_BLOCK_PARTS.filter((row) => parts.includes(row.id)).map((row) => row.label).join(", ");
  const name = blocked ? `Agenda bloqué : ${caption}` : "Bloquer l’agenda";
  return (
    <>
      <button
        type="button"
        className={[
          "grid h-7 w-7 shrink-0 place-items-center rounded-full",
          blocked ? "bg-amber-500 text-white" : "text-slate-400 hover:bg-black/5",
        ].join(" ")}
        aria-label={name}
        title={name}
        onClick={() => setOpen(true)}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <LockIcon />
      </button>
      {open ? (
        <DayBlockSheet
          day={day}
          label={label}
          parts={parts}
          linked={linked}
          ready={ready}
          onChange={onChange}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function DayBlockSheet({
  day,
  label,
  parts,
  linked,
  ready,
  onChange,
  onClose,
}: {
  day: string;
  label: string;
  parts: DayPart[];
  linked: boolean;
  ready: boolean;
  onChange: (day: string, parts: DayPart[], linked: boolean, ready: boolean) => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<DayPart | "">("");
  const [error, setError] = useState("");

  async function toggle(part: DayPart) {
    if (busy) return;
    setBusy(part);
    setError("");
    try {
      const next = await setDayBlock(day, part, !parts.includes(part));
      onChange(day, next.parts, next.linked, next.ready);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "L’agenda n’a pas enregistré le blocage.");
    } finally {
      setBusy("");
    }
  }

  return (
    <Modal onClose={onClose} closeDisabled={busy !== ""} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {() => (
        <div>
          <h3 className="crm-title pr-8 text-lg font-semibold">Bloquer l’agenda</h3>
          <p className="mt-1 text-sm capitalize text-slate-500">{label}</p>
          <p className="mt-3 text-sm text-slate-600">Ça bloque l’agenda pour les rendez-vous. Aucune tâche n’est créée. Mes RDV ne proposera plus ces créneaux.</p>
          <p className="mt-2 text-xs text-slate-500">Un second clic sur la même plage retire le blocage.</p>
          {ready && !linked ? <p className="mt-2 text-sm text-amber-700">Tes types de RDV n’ont pas encore d’agenda branché. Le blocage est posé sur tes agendas ; Mes RDV le prendra en compte dès qu’un agenda est choisi sur le type.</p> : null}
          {!ready ? <p className="mt-2 text-sm text-amber-700">Aucun agenda sur ce compte. Le blocage ne peut pas être posé.</p> : null}
          {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
          <div className="mt-4 flex flex-col gap-2">
            {DAY_BLOCK_PARTS.map((row) => {
              const on = parts.includes(row.id);
              return (
                <button
                  key={row.id}
                  type="button"
                  className={[
                    "flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold disabled:opacity-50",
                    on ? "border border-amber-600 bg-amber-500 text-white" : "crm-pill",
                  ].join(" ")}
                  disabled={busy !== "" || !ready}
                  onClick={() => void toggle(row.id)}
                >
                  <span className="min-w-0">
                    <span className="block">{row.label}</span>
                    <span className={on ? "block text-xs font-normal opacity-80" : "block text-xs font-normal text-slate-500"}>{row.hours}{row.note ? ` · ${row.note}` : ""}</span>
                  </span>
                  <span className="shrink-0 text-xs">{busy === row.id ? "…" : on ? "Retirer" : "Bloquer"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path strokeLinecap="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
